import { Link, usePage, router } from "@inertiajs/react"
import { Bell, LogOut, Check } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import { UserDisplay } from "@/components/user-display"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { usePageHeader } from "@/hooks/use-page-header"
import { useNotificationChannel } from "@/hooks/use-notification-channel"
import { ThemeToggle } from "@/components/theme-toggle"
import { getNotificationMessage, getNotificationIcon } from "@/lib/notification-messages"
import type { SharedProps, AppNotification } from "@/types"

function timeAgo(dateString: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000)
  if (seconds < 60) return "à l'instant"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `il y a ${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `il y a ${hours}h`
  const days = Math.floor(hours / 24)
  return `il y a ${days}j`
}

export function AppHeader() {
  const { current_user, unread_notifications_count } = usePage<SharedProps>().props
  const pageHeader = usePageHeader()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [open, setOpen] = useState(false)

  const fetchNotifications = useCallback(() => {
    fetch("/notifications.json")
      .then((res) => res.json())
      .then((data) => setNotifications(data.notifications || []))
      .catch(() => {})
  }, [])

  useNotificationChannel({
    onNotification: () => {
      if (open) fetchNotifications()
    },
  })

  useEffect(() => {
    if (open) fetchNotifications()
  }, [open, fetchNotifications])

  const markAsRead = (id: string) => {
    router.patch(
      `/notifications/${id}`,
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)))
        },
      },
    )
  }

  const markAllAsRead = () => {
    router.post(
      "/notifications/read_all",
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })))
        },
      },
    )
  }

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
      <SidebarTrigger />
      <Separator orientation="vertical" className="h-4" />
      {pageHeader && (
        <div className="flex flex-1 items-center gap-2">
          {pageHeader.back ? (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href={pageHeader.back}>{pageHeader.backLabel ?? "Retour"}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{pageHeader.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          ) : (
            <h1 className="text-lg font-semibold">{pageHeader.title}</h1>
          )}
          {pageHeader.extra}
        </div>
      )}
      <div className="ml-auto flex items-center gap-1">
        <ThemeToggle />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="size-4" />
              {unread_notifications_count > 0 && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 size-4 justify-center p-0 text-[10px]">
                  {unread_notifications_count > 9 ? "9+" : unread_notifications_count}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <PopoverHeader className="flex-row items-center justify-between px-3 py-2">
              <PopoverTitle>Notifications</PopoverTitle>
              {unread_notifications_count > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllAsRead}>
                  <Check className="mr-1 size-3" />
                  Tout marquer comme lu
                </Button>
              )}
            </PopoverHeader>
            <Separator />
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-sm">Aucune notification</p>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    className={`flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm cursor-pointer transition-colors hover:bg-muted/50 ${
                      !notification.read_at ? "bg-muted/30" : ""
                    }`}
                    onClick={() => {
                      if (!notification.read_at) markAsRead(notification.id)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !notification.read_at) markAsRead(notification.id)
                    }}
                  >
                    {notification.data.actor_first_name && notification.data.actor_last_name ? (
                      <UserDisplay
                        user={{
                          first_name: notification.data.actor_first_name,
                          last_name: notification.data.actor_last_name,
                        }}
                        size="sm"
                        avatarOnly
                      />
                    ) : (
                      (() => {
                        const Icon = getNotificationIcon(notification.notification_type)
                        return <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      })()
                    )}
                    <div className="flex-1">
                      <p className={!notification.read_at ? "font-medium" : ""}>
                        {getNotificationMessage(notification.notification_type, notification.data)}
                      </p>
                      <p className="text-muted-foreground text-xs mt-0.5">{timeAgo(notification.created_at)}</p>
                    </div>
                    {!notification.read_at && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />}
                  </div>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
        {current_user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <UserDisplay user={current_user} size="sm" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">{current_user.full_name}</p>
                  <p className="text-muted-foreground text-xs">{current_user.email_address}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => router.delete("/session")}>
                <LogOut />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}
